/**
 * article-enrichment.js
 *
 * Progressively enhances inline product links in blog articles with a
 * hover/tap preview card. Purely client-side; SEO-relevant content (the
 * "Im Artikel erwähnt" grid and JSON-LD mentions) is rendered server-side
 * by the `article-product-enrichment` snippet.
 *
 * Scope: any `a[href^="/products/"]` within `.rte[itemprop="articleBody"]`.
 *
 * Behaviour:
 *   - Desktop (hover-capable pointer): hover delay ~260ms → tooltip above/below.
 *   - Touch: link itself navigates on tap; a small ⓘ affordance opens an
 *     inline peek card below the paragraph instead of a hover tooltip.
 *
 * Data source: /products/<handle>.js — fetched once per handle, cached in
 * sessionStorage to cut repeat requests across page views.
 */

(function () {
  'use strict';

  if (window.__articleEnrichmentInit) return;
  window.__articleEnrichmentInit = true;

  var HOVER_DELAY_MS = 260;
  var HIDE_DELAY_MS = 180;
  var CACHE_PREFIX = 'bf:aep:';
  var CACHE_TTL_MS = 1000 * 60 * 60 * 6; // 6 hours
  var fetching = Object.create(null);

  var isTouch = matchMedia('(hover: none) and (pointer: coarse)').matches;

  var formatter = null;
  try {
    formatter = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR'
    });
  } catch (_) { /* fall through to raw price */ }

  function formatPrice(cents) {
    if (typeof cents !== 'number') return '';
    var value = cents / 100;
    if (formatter) {
      try { return formatter.format(value); } catch (_) {}
    }
    return value.toFixed(2) + ' €';
  }

  function cacheGet(handle) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + handle);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.d;
    } catch (_) { return null; }
  }

  function cacheSet(handle, data) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + handle, JSON.stringify({ t: Date.now(), d: data }));
    } catch (_) { /* quota exceeded — ignore */ }
  }

  function fetchProduct(handle) {
    var cached = cacheGet(handle);
    if (cached) return Promise.resolve(cached);
    if (fetching[handle]) return fetching[handle];

    var url = '/products/' + encodeURIComponent(handle) + '.js';
    var promise = fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (raw) {
        var slim = {
          title: raw.title,
          vendor: raw.vendor,
          price: raw.price,
          price_min: raw.price_min,
          price_max: raw.price_max,
          available: raw.available,
          url: raw.url,
          featured_image: raw.featured_image,
          images: (raw.images || []).slice(0, 1)
        };
        cacheSet(handle, slim);
        return slim;
      })
      .catch(function (err) {
        delete fetching[handle];
        throw err;
      });

    fetching[handle] = promise;
    return promise;
  }

  /* ---------- Tooltip instance (desktop) --------- */

  var tooltip = null;
  var tooltipFor = null;
  var hideTimer = null;

  function ensureTooltip() {
    if (tooltip) return tooltip;
    tooltip = document.createElement('div');
    tooltip.className = 'article-enrichment-preview';
    tooltip.setAttribute('role', 'tooltip');
    tooltip.addEventListener('mouseenter', function () {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    });
    tooltip.addEventListener('mouseleave', scheduleHide);
    document.body.appendChild(tooltip);
    return tooltip;
  }

  function renderCard(p) {
    var img = (p.featured_image && p.featured_image) || (p.images && p.images[0]);
    var imgUrl = typeof img === 'string' ? img : (img && img.src ? img.src : '');
    // Upgrade Shopify CDN images to width=480 for a sharp but small preview
    if (imgUrl && /cdn\.shopify\.com/.test(imgUrl)) {
      imgUrl = imgUrl.replace(/(\.(?:jpg|jpeg|png|webp))(\?.*)?$/, '_480x$1$2');
    } else if (imgUrl && imgUrl.indexOf('//') === 0) {
      imgUrl = 'https:' + imgUrl;
    }

    var priceHtml = '';
    if (typeof p.price === 'number') {
      if (p.price_min != null && p.price_max != null && p.price_min !== p.price_max) {
        priceHtml = 'ab ' + formatPrice(p.price_min);
      } else {
        priceHtml = formatPrice(p.price);
      }
    }

    var availHtml = p.available
      ? ''
      : '<span class="article-enrichment-preview__availability article-enrichment-preview__availability--sold-out">ausverkauft</span>';

    return (
      (imgUrl
        ? '<div class="article-enrichment-preview__media"><img src="' + escapeAttr(imgUrl) + '" alt="' + escapeAttr(p.title || '') + '" loading="lazy" decoding="async"></div>'
        : '') +
      '<div class="article-enrichment-preview__info">' +
        (p.vendor ? '<span class="article-enrichment-preview__vendor">' + escapeHtml(p.vendor) + '</span>' : '') +
        '<span class="article-enrichment-preview__title">' + escapeHtml(p.title || '') + '</span>' +
        '<span class="article-enrichment-preview__price">' + priceHtml + availHtml + '</span>' +
        '<a class="article-enrichment-preview__cta" href="' + escapeAttr(p.url || '#') + '">Zum Garn</a>' +
      '</div>'
    );
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  function positionTooltip(anchor) {
    if (!tooltip) return;
    var rect = anchor.getBoundingClientRect();
    var tipRect = tooltip.getBoundingClientRect();
    var scrollX = window.pageXOffset;
    var scrollY = window.pageYOffset;
    var margin = 12;

    var spaceBelow = window.innerHeight - rect.bottom;
    var placeBelow = spaceBelow > tipRect.height + margin || rect.top < tipRect.height + margin;

    var top = placeBelow
      ? rect.bottom + scrollY + 8
      : rect.top + scrollY - tipRect.height - 8;

    var leftPref = rect.left + scrollX + (rect.width / 2) - (tipRect.width / 2);
    var maxLeft = scrollX + window.innerWidth - tipRect.width - margin;
    var minLeft = scrollX + margin;
    var left = Math.max(minLeft, Math.min(leftPref, maxLeft));

    tooltip.style.top = top + 'px';
    tooltip.style.left = left + 'px';
  }

  function scheduleHide() {
    if (hideTimer) clearTimeout(hideTimer);
    hideTimer = setTimeout(function () {
      if (tooltip) tooltip.setAttribute('data-visible', 'false');
      tooltipFor = null;
    }, HIDE_DELAY_MS);
  }

  function showTooltipFor(anchor) {
    var handle = anchor.dataset.productHandle;
    if (!handle) return;
    var tip = ensureTooltip();
    if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    tooltipFor = anchor;

    tip.setAttribute('data-loading', 'true');
    tip.setAttribute('data-visible', 'true');
    tip.innerHTML =
      '<div class="article-enrichment-preview__info">' +
        '<span class="article-enrichment-preview__title">' + escapeHtml(anchor.textContent.trim()) + '</span>' +
        '<span class="article-enrichment-preview__price">\u2026</span>' +
      '</div>';
    positionTooltip(anchor);

    fetchProduct(handle)
      .then(function (p) {
        if (tooltipFor !== anchor) return;
        tip.innerHTML = renderCard(p);
        tip.removeAttribute('data-loading');
        positionTooltip(anchor);
      })
      .catch(function () {
        if (tooltipFor === anchor && tip) {
          tip.setAttribute('data-visible', 'false');
        }
      });
  }

  /* ---------- Mobile inline peek --------- */

  function togglePeek(anchor, toggleButton) {
    // Find an existing peek that belongs to this link (skip the ⓘ button itself
    // and any whitespace text nodes).
    var cursor = anchor.nextElementSibling;
    while (cursor && cursor !== toggleButton) { cursor = cursor.nextElementSibling; }
    var existing = cursor ? cursor.nextElementSibling : null;
    while (existing && existing.nodeType === 3) { existing = existing.nextElementSibling; }
    if (existing && existing.classList && existing.classList.contains('article-enrichment-preview')) {
      // Second tap: fully remove so no layout gap remains.
      existing.parentNode.removeChild(existing);
      return false;
    }
    var peek = document.createElement('div');
    peek.className = 'article-enrichment-preview';
    peek.setAttribute('data-visible', 'true');
    var insertAfter = toggleButton || anchor;
    insertAfter.parentNode.insertBefore(peek, insertAfter.nextSibling);
    populatePeek(anchor, peek);
    return true;
  }

  function populatePeek(anchor, peek) {
    var handle = anchor.dataset.productHandle;
    if (!handle) return;
    peek.setAttribute('data-loading', 'true');
    peek.innerHTML =
      '<div class="article-enrichment-preview__info">' +
        '<span class="article-enrichment-preview__title">' + escapeHtml(anchor.textContent.trim()) + '</span>' +
        '<span class="article-enrichment-preview__price">\u2026</span>' +
      '</div>';
    fetchProduct(handle).then(function (p) {
      peek.innerHTML = renderCard(p);
      peek.removeAttribute('data-loading');
      peek.dataset.loaded = '1';
    }).catch(function () {
      peek.setAttribute('data-visible', 'false');
    });
  }

  /* ---------- Wire up links --------- */

  function enhanceLink(a) {
    if (a.dataset.aepEnhanced) return;
    var href = a.getAttribute('href') || '';
    // Accept both relative ("/products/foo") and absolute
    // ("https://bonifaktur.de/products/foo", "//example.com/products/foo") URLs.
    var match = href.match(/(?:^|\/\/[^\/]+)\/products\/([a-z0-9][a-z0-9-_]*)(?:[\/?#].*)?$/i);
    if (!match) return;
    var handle = match[1].toLowerCase();

    a.dataset.aepEnhanced = '1';
    a.dataset.productHandle = handle;
    a.classList.add('article-enrichment-link');

    if (isTouch) {
      // Add an ⓘ affordance button so tap-on-link keeps navigating.
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'article-enrichment-link__peek';
      btn.setAttribute('aria-label', 'Vorschau anzeigen');
      btn.setAttribute('aria-expanded', 'false');
      btn.textContent = '\u00A0ⓘ';
      btn.style.cssText = 'background:none;border:none;cursor:pointer;font:inherit;color:inherit;opacity:0.55;padding:0 0.15rem;';
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var nowOpen = togglePeek(a, btn);
        btn.setAttribute('aria-expanded', nowOpen ? 'true' : 'false');
      });
      if (a.parentNode) a.parentNode.insertBefore(btn, a.nextSibling);
      return;
    }

    var showTimer = null;
    a.addEventListener('mouseenter', function () {
      if (showTimer) clearTimeout(showTimer);
      showTimer = setTimeout(function () { showTooltipFor(a); }, HOVER_DELAY_MS);
    });
    a.addEventListener('mouseleave', function () {
      if (showTimer) { clearTimeout(showTimer); showTimer = null; }
      scheduleHide();
    });
    a.addEventListener('focus', function () { showTooltipFor(a); });
    a.addEventListener('blur', scheduleHide);
  }

  function init() {
    var body = document.querySelector('.rte[itemprop="articleBody"]');
    if (!body) return;
    // Widen selector to catch both relative and absolute product URLs.
    // `enhanceLink` re-validates with a stricter regex before attaching handlers.
    var links = body.querySelectorAll('a[href*="/products/"]');
    Array.prototype.forEach.call(links, enhanceLink);

    // Reposition tooltip on scroll/resize while visible
    var rafScheduled = false;
    function refresh() {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(function () {
        rafScheduled = false;
        if (tooltip && tooltipFor && tooltip.getAttribute('data-visible') === 'true') {
          positionTooltip(tooltipFor);
        }
      });
    }
    window.addEventListener('scroll', refresh, { passive: true });
    window.addEventListener('resize', refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
